/** @deprecated Legacy-Tag – wird durch DockSite-Leistung / DockSite-Sonderleistung ersetzt. */
export const DOCKSITE_LEGACY_PRODUCT_TAG = 'DockSite';

export const DOCKSITE_MATERIAL_PRODUCT_TAG = 'DockSite-Material';
export const DOCKSITE_SERVICE_PRODUCT_TAG = 'DockSite-Leistung';
export const DOCKSITE_SPECIAL_SERVICE_PRODUCT_TAG = 'DockSite-Sonderleistung';

/** Alias für bestehende Imports. */
export const DOCKSITE_PRODUCT_TAG = DOCKSITE_LEGACY_PRODUCT_TAG;

export const DOCKSITE_CLASSIFICATION_PRODUCT_TAGS = [
    DOCKSITE_MATERIAL_PRODUCT_TAG,
    DOCKSITE_SERVICE_PRODUCT_TAG,
    DOCKSITE_SPECIAL_SERVICE_PRODUCT_TAG
] as const;

export enum DockSiteProductClassification {
    None = 'none',
    Material = 'material',
    Service = 'service',
    SpecialService = 'special_service'
}

export interface DockSiteProductClassificationOptions {
    isDocksiteWeighing?: boolean;
    isDocksiteSlipping?: boolean;
}

function resolveProductTagNames(tags: Array<{ name?: string } | string> | null | undefined): string[] {
    return (tags || []).map(tag => (typeof tag === 'string' ? tag : tag?.name)).filter(Boolean) as string[];
}

function normalizeProductTagName(tag: string): string {
    return tag.trim().toLowerCase();
}

export function isLegacyDockSiteProductTag(tag: string): boolean {
    return normalizeProductTagName(tag) === DOCKSITE_LEGACY_PRODUCT_TAG.toLowerCase();
}

export function isDockSiteClassificationProductTag(tag: string): boolean {
    const normalized = normalizeProductTagName(tag);

    return (
        DOCKSITE_CLASSIFICATION_PRODUCT_TAGS.some(classificationTag => classificationTag.toLowerCase() === normalized) ||
        isLegacyDockSiteProductTag(tag)
    );
}

export function resolveDockSiteProductClassification(
    tags: Array<{ name?: string } | string> | null | undefined,
    options: DockSiteProductClassificationOptions = {}
): DockSiteProductClassification {
    const tagNames = resolveProductTagNames(tags);

    if (tagNames.some(tag => normalizeProductTagName(tag) === DOCKSITE_MATERIAL_PRODUCT_TAG.toLowerCase())) {
        return DockSiteProductClassification.Material;
    }

    if (
        tagNames.some(tag => normalizeProductTagName(tag) === DOCKSITE_SPECIAL_SERVICE_PRODUCT_TAG.toLowerCase())
    ) {
        return DockSiteProductClassification.SpecialService;
    }

    if (tagNames.some(tag => normalizeProductTagName(tag) === DOCKSITE_SERVICE_PRODUCT_TAG.toLowerCase())) {
        return DockSiteProductClassification.Service;
    }

    if (tagNames.some(isLegacyDockSiteProductTag)) {
        if (options.isDocksiteWeighing || options.isDocksiteSlipping) {
            return DockSiteProductClassification.SpecialService;
        }

        return DockSiteProductClassification.Service;
    }

    return DockSiteProductClassification.None;
}

export function resolveDockSiteClassificationProductTagName(
    classification: DockSiteProductClassification
): string | null {
    switch (classification) {
        case DockSiteProductClassification.Material:
            return DOCKSITE_MATERIAL_PRODUCT_TAG;
        case DockSiteProductClassification.Service:
            return DOCKSITE_SERVICE_PRODUCT_TAG;
        case DockSiteProductClassification.SpecialService:
            return DOCKSITE_SPECIAL_SERVICE_PRODUCT_TAG;
        default:
            return null;
    }
}

export function applyDockSiteClassificationToProductTags<T extends { id?: number; name: string }>(
    tags: readonly T[],
    classification: DockSiteProductClassification
): T[] {
    const withoutDockSiteTags = tags.filter(tag => !isDockSiteClassificationProductTag(tag.name));
    const tagName = resolveDockSiteClassificationProductTagName(classification);

    if (!tagName) {
        return [...withoutDockSiteTags];
    }

    return [...withoutDockSiteTags, { name: tagName, id: -1 } as T];
}

export function productHasAnyDockSiteClassificationTag(
    tags: Array<{ name?: string } | string> | null | undefined
): boolean {
    return resolveDockSiteProductClassification(tags) !== DockSiteProductClassification.None;
}

export function productHasDockSiteMaterialTag(
    tags: Array<{ name?: string } | string> | null | undefined
): boolean {
    return resolveDockSiteProductClassification(tags) === DockSiteProductClassification.Material;
}

export function productHasDockSiteServiceTag(
    tags: Array<{ name?: string } | string> | null | undefined,
    options: DockSiteProductClassificationOptions = {}
): boolean {
    return resolveDockSiteProductClassification(tags, options) === DockSiteProductClassification.Service;
}

export function productHasDockSiteSpecialServiceTag(
    tags: Array<{ name?: string } | string> | null | undefined,
    options: DockSiteProductClassificationOptions = {}
): boolean {
    return resolveDockSiteProductClassification(tags, options) === DockSiteProductClassification.SpecialService;
}

export function productHasDockSiteLeistungTabTag(
    tags: Array<{ name?: string } | string> | null | undefined,
    options: DockSiteProductClassificationOptions = {}
): boolean {
    const classification = resolveDockSiteProductClassification(tags, options);

    return (
        classification === DockSiteProductClassification.Service ||
        classification === DockSiteProductClassification.SpecialService
    );
}

export function resolveDockSiteProductFilterTags(classifications: DockSiteProductClassification[]): string[] {
    const tags = classifications
        .map(resolveDockSiteClassificationProductTagName)
        .filter((tag): tag is string => !!tag);

    return [...new Set([...tags, DOCKSITE_LEGACY_PRODUCT_TAG])];
}

export const DOCKSITE_PRODUCT_CLASSIFICATION_FILTER_OPTIONS = [
    DockSiteProductClassification.None,
    DockSiteProductClassification.Material,
    DockSiteProductClassification.Service,
    DockSiteProductClassification.SpecialService,
] as const;

export function resolveDockSiteProductClassificationTranslationKey(
    classification: DockSiteProductClassification
): string {
    switch (classification) {
        case DockSiteProductClassification.Material:
            return 'table.dockSiteClassification.material';
        case DockSiteProductClassification.Service:
            return 'table.dockSiteClassification.service';
        case DockSiteProductClassification.SpecialService:
            return 'table.dockSiteClassification.specialService';
        default:
            return '';
    }
}

export const dockSiteClassificationFilterTranslationKeys: Record<DockSiteProductClassification, string> = {
    [DockSiteProductClassification.None]: 'table.dockSiteClassification.filterNone',
    [DockSiteProductClassification.Material]: 'table.dockSiteClassification.material',
    [DockSiteProductClassification.Service]: 'table.dockSiteClassification.service',
    [DockSiteProductClassification.SpecialService]: 'table.dockSiteClassification.specialService',
};
